import { useState, useCallback, useRef } from 'react'
import { useToast } from '@/modules/shared/hooks/use-toast'
import { entitySearchService, type EntitySearchPayload } from '../services/entity-search.service'
import type { Entity } from '@/modules/shared/types/entity.type'
import { useAuthStore } from '@/modules/shared/stores/auth.store'

export function useEntitySearch() {
  const { toast } = useToast()
  const { versionUrl } = useAuthStore()
  const isApiV3 = versionUrl.includes('/v3')

  const [entities, setEntities] = useState<Entity[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)

  const currentSearchRef = useRef<EntitySearchPayload | null>(null)

  const searchEntities = useCallback(async (payload: Omit<EntitySearchPayload, 'dataInclusao'>, dataInclusao?: string) => {
    const searchPayload: EntitySearchPayload = {
      ...payload,
      ...(isApiV3 && dataInclusao && { dataInclusao }),
      inputValue: payload.filter === 'cnpjcpf' || payload.filter === 'id_entidade'
        ? payload.inputValue.replace(/\D/g, '')
        : payload.inputValue
    }

    if (searchPayload.inputValue?.length <= 0 || searchPayload.inputValue == undefined) {
      searchPayload.inputValue = '%20'
    }

    setIsLoading(true)
    setEntities([])
    setCurrentPage(1)
    setHasMore(false)
    setTotalRecords(0)
    currentSearchRef.current = searchPayload

    try {
      const result = await entitySearchService.searchInitialEntities(searchPayload)
      if (currentSearchRef.current !== searchPayload) return
      setEntities(result.entities)
      setTotalRecords(result.totalRecords)
      setHasMore(result.hasMore)
      setCurrentPage(2)
    } catch (error) {
      if (currentSearchRef.current !== searchPayload) return
      toast.error({
        title: 'Erro ao buscar entidades',
        description: 'Tente novamente em alguns instantes'
      })
      setEntities([])
      setHasMore(false)
      setTotalRecords(0)
    } finally {
      if (currentSearchRef.current === searchPayload) setIsLoading(false)
    }
  }, [toast, isApiV3])

  const loadMore = useCallback(async () => {
    if (!currentSearchRef.current || isLoadingMore || !hasMore) return

    setIsLoadingMore(true)

    try {
      const result = await entitySearchService.loadMoreEntities(
        currentSearchRef.current,
        currentPage,
        entities.length,
        totalRecords
      )

      if (result.entities.length > 0) {
        setEntities(prev => [...prev, ...result.entities])
        setCurrentPage(prev => prev + 1)
      }

      setHasMore(result.hasMore)
    } catch (error) {
      toast.error({
        title: 'Erro ao carregar mais entidades',
      })
    } finally {
      setIsLoadingMore(false)
    }
  }, [currentPage, hasMore, isLoadingMore, entities.length, totalRecords, toast])

  const resetSearch = useCallback(() => {
    setEntities([])
    setIsLoading(false)
    setIsLoadingMore(false)
    setHasMore(false)
    setTotalRecords(0)
    setCurrentPage(1)
    currentSearchRef.current = null
  }, [])

  return {
    entities,
    isLoading,
    isLoadingMore,
    hasMore,
    totalRecords,
    searchEntities,
    loadMore,
    resetSearch
  }
}